/* eslint-disable camelcase */
import { FastField, Formik } from "formik"
import _ from "lodash"
import { observer } from "mobx-react-lite"
import * as React from "react"
import { Appearance, ViewStyle, Platform } from "react-native"
import { Farm, Pond } from "../../../database"
import { translate, TxKeyPath } from "../../../i18n"
import { color, nativeBaseTheme } from "../../../theme"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { ModelPicker, ModelPickerProps } from "../../model-picker/model-picker"
import { FormProps } from "../form"
import * as yup from "yup"
import { BluetoothPicker, BluetoothPickerProps } from "../../bluetooth-picker/bluetooth-picker"
import { View, Modal, useDisclose } from 'native-base'
import RNBluetoothClassic from "react-native-bluetooth-classic";
import { Text } from "../../text/text"
import { Button } from "../../button/button"
import { navigate } from "../../../navigators"

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  alignSelf: "center",
  paddingVertical: 6,
  paddingHorizontal: 16,
  borderRadius: 38
}

export interface AutoFeederPondFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
   initialValues?: {
    farm_id?: number
    pond_id?: number
    auto_feeder_address?: string
    name?: string
  },
  params?: any
}

const ModelField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, modelPickerProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`autoFeederPondForm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_CONTROL}
        {...formControlProps}
      >
        <ModelPicker
          colorScheme={colorScheme}
          placeholderTx={`autoFeederPondForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={async (value) => {
            await setFieldValue(field.name, value)
            await setFieldTouched(field.name)
          }}
          isUseSearch
          {...modelPickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

const BluetoothField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, bluetoothPickerProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, bluetoothPickerProps?: (Omit<BluetoothPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`autoFeederPondForm.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_CONTROL}
        {...formControlProps}
      >
        <BluetoothPicker
          colorScheme={colorScheme}
          placeholderTx={`autoFeederPondForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={async (value) => {
            await setFieldValue(field.name, value)
            await setFieldTouched(field.name)
          }}
          {...bluetoothPickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

// const TextField = (
//   { name, input = {}, nextInput = {}, colorScheme = Appearance.getColorScheme(), formControlProps, inputProps }:
//   { name: string, input: any, nextInput: any, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, inputProps?: InputProps },
// ) => {
//   const { inputProps: formControlInputProps, ...formControlRestProps } = formControlProps
//   return (
//     <FastField name={name}>
//       {({ field, form: { handleChange, handleBlur }, meta }) => (
//         <FormControl
//           isInvalid={meta.touched && !!meta.error}
//           labelTx={`autoFeederPondForm.${_.camelCase(field.name)}` as TxKeyPath}
//           colorScheme={colorScheme}
//           inputProps={{
//             testID: `jala:id/${field.name}-input`,
//             ref: input[field.name],
//             autoCapitalize: "none",
//             autoComplete: "name",
//             autoCorrect: false,
//             textContentType: "name",
//             keyboardType: "default",
//             returnKeyType: nextInput[field.name] ? "next" : "go",
//             onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : null,
//             placeholderTx: `autoFeederPondForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
//             defaultValue: meta.initialValue,
//             onChangeText: (text) => handleChange(field.name)(text.trim()),
//             onBlur: handleBlur(field.name),
//             ...formControlInputProps,
//             ...inputProps
//           }}
//           errorMessage={meta.error}
//           style={FORM_CONTROL}
//           {...formControlRestProps}
//         />
//       )}
//     </FastField>
//   )
// }

/**
 * Describe your component here
 */
export const AutoFeederPondForm = observer(function AutoFeederPondForm(props: AutoFeederPondFormProps) {
  const {
    initialValues = {}, onSubmit, colorScheme = Appearance.getColorScheme(), innerRef, params ,
    ...rest
  } = props

  const { isOpen, onClose, onOpen } = useDisclose()

  // const input = {
  //   //
  // }

  // const nextInput = {
  //   //
  // }
  React.useEffect(() => {
    checkBluetooth()
  }, [])

  const checkBluetooth = async () => {
    try {
      const available = await RNBluetoothClassic.isBluetoothEnabled();
      if (!available) onOpen()
    } catch (err) {
      console.log(err, 'unavailable bluetooth')
      // Handle accordingly
    }
  }

  const onConfirm = React.useCallback(async () => {
    if(Platform.OS === 'android') await RNBluetoothClassic.requestBluetoothEnabled();
    onClose()
  }, [onClose])

  const onNavigateToQR = React.useCallback(() => {
    navigate("Home.Stack", "Home.AutofeederPond.Create.QRScan", { device: {} })
  }, [])

  const validationSchema = yup.object({
    farm_id: yup.number().required().label(translate("autoFeederPondForm.farmId")),
    pond_id: yup.number().required().label(translate("autoFeederPondForm.pondId")),
    auto_feeder_address: yup.string().nullable().required().label(translate("autoFeederPondForm.autoFeeder")),
    name: yup.string().ascii().nullable().label(translate("autoFeederPondForm.name")),
  })

  return (
    <Formik
      enableReinitialize
      initialValues={{
        farm_id: null,
        pond_id: null,
        auto_feeder_address: null,
        name: null,
        ...initialValues,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('pond_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id');
              },
              InputRightElement: !!initialValues.farm_id && <></> || undefined,
              style: { borderWidth: 0, backgroundColor: color.palette.white}
            }}
            
          />
          <View style={BORDER} />
          <ModelField
            key={`${values.farm_id}-pond_id`}
            name="pond_id"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Pond,
              dataLabel: "name",
              fetchData: (model, search) => (
                model.fetch({
                  farm_id__eq: values.farm_id,
                  name__contains: search,
                  sort: 'name_alpha_numeric'
                })
              ),
              renderSelectedItemText: (item) => item && item.name,
              clearable: true,
              isDisabled: !values.farm_id || !!initialValues.pond_id
            }}
          />
          <BluetoothField
            name="auto_feeder_address"
            colorScheme={colorScheme}
            formControlProps={{
              isRequired: true,
              labelTx: "autoFeederPondForm.autoFeeder",
            }}
            bluetoothPickerProps={{
              clearable: true,
              placeholderTx: "autoFeederPondForm.autoFeederPlaceholder",
              onValueChange: async (value, index, item) => {
                await setFieldValue('name', item && item.name || null)
                await setFieldValue('auto_feeder_address', value || null)
                await setFieldTouched('auto_feeder_address')
              },
              onPressBluetooth: checkBluetooth,
              onPressQRScan: onNavigateToQR
              // InputRightElement: () => <Icon name="bluetooth" />
            }}
          />
          <Modal isOpen={isOpen} onClose={onClose}>
            <View
              alignSelf="stretch"
              marginY="80px"
              marginX="28px"
              paddingY="24px"
              paddingX="16px"
              rounded="16px"
              backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
            >
              <View alignItems="center" marginBottom="12px">
                <Text
                  text={translate("autoFeederPondForm.enableBluetoothTitle")}
                  transformText={_.upperFirst}
                  preset="title1"
                  colorScheme={colorScheme}
                  marginY="8px" marginX="7px"
                  color={color.primaryDarker}
                />
                <Text
                  text={translate("autoFeederPondForm.enableBluetoothSubtitle")}
                  transformText={_.upperFirst}
                  preset="body2"
                  colorScheme={colorScheme}
                  marginBottom="8px"
                  textAlign="center"
                />
              </View>
              <View flexDirection="row" justifyContent="center">
                {Platform.OS === 'android' && <Button
                  testID="jala:id/cancel-bluetooth-modal"
                  text={translate("autoFeederPondForm.enableBluetoothCancel")}
                  preset="outline"
                  textPreset="title4"
                  colorScheme={colorScheme}
                  style={BUTTON_DETAIL_ITEM}
                  onPress={onClose}
                />}
                <View width="12px" />
                <Button
                  testID="jala:id/confirm-bluetootj-modal"
                  text={Platform.OS !== 'ios' ? translate("autoFeederPondForm.enableBluetoothConfirm") : translate('common.ok')}
                  preset="primary"
                  textPreset="title4"
                  colorScheme={colorScheme}
                  style={BUTTON_DETAIL_ITEM}
                  onPress={onConfirm}
                />
              </View>
            </View>
          </Modal>
          {/* <TextField */}
          {/*   name="name" */}
          {/*   colorScheme={colorScheme} */}
          {/*   input={input} */}
          {/*   nextInput={nextInput} */}
          {/*   formControlProps={{ isRequired: true, isDisabled: true }} */}
          {/* /> */}
        </>
      )}
    </Formik>
  )
})

AutoFeederPondForm.displayName = 'AutoFeederPondForm';
